import { calendarDteAtCursor, expiryCloseMs, toET } from "@/lib/market/hours";
import { resolveLegExpiry } from "@/lib/options/leg-premium";
import { payoffAtExpiry } from "@/lib/options/payoff";
import { encodeLegsAsCustomParams } from "@/lib/strategy/flatten";
import {
  applyLegDeltas,
  applyLegOverrides,
  effectiveSpot,
} from "@/lib/strategy/leg-deltas";
import { getRegistryEntry } from "@/lib/strategy/registry";
import type { OptionLeg } from "@/types/options";
import type { StrategyLayer, StrategyLayerKind } from "@/types/strategy";
import { generalTemplateParams } from "./template-preview";

export interface GeneralLeg extends OptionLeg {
  id: string;
  expiry: string;
  premium: number | null;
}
export interface GeneralDraft {
  id: string;
  name: string;
  structureKind?: StrategyLayerKind;
  referencePrice?: number;
  strikeOffset?: number;
  legs: GeneralLeg[];
  quantity: number;
  enabled: boolean;
  color: string;
  source?: { layer: StrategyLayer; legs: GeneralLeg[] };
}
// Persisted numeric codes use this order (1-based); append, never reorder.
export const GENERAL_KINDS: StrategyLayerKind[] = [
  "iron_condor",
  "bull_call_spread",
  "bear_put_spread",
  "butterfly",
  "single_call",
  "single_put",
  "straddle",
  "custom",
];

export function generalDraftKind(draft: GeneralDraft): StrategyLayerKind {
  const kind = draft.structureKind ?? draft.source?.layer.kind ?? "custom";
  const legs = draft.legs;
  const equal =
    legs.length > 0 && legs.every((leg) => leg.quantity === legs[0].quantity);
  const calls = legs.filter((leg) => leg.type === "call").length;
  const puts = legs.filter((leg) => leg.type === "put").length;
  let valid = true;
  switch (kind) {
    case "iron_condor":
      valid = legs.length === 4 && calls === 2 && puts === 2 && equal;
      break;
    case "bull_call_spread":
      valid = legs.length === 2 && calls === 2 && equal;
      break;
    case "bear_put_spread":
      valid = legs.length === 2 && puts === 2 && equal;
      break;
    case "single_call":
      valid = legs.length === 1 && calls === 1;
      break;
    case "single_put":
      valid = legs.length === 1 && puts === 1;
      break;
    case "straddle":
      valid = legs.length === 2 && calls === 1 && puts === 1 && equal;
      break;
    case "butterfly": {
      const quantities = legs.map((leg) => leg.quantity).sort((a, b) => a - b);
      valid =
        legs.length === 3 &&
        (calls === 3 || puts === 3) &&
        quantities[0] === quantities[1] &&
        quantities[2] === quantities[0] * 2;
      break;
    }
  }
  // Direction is deliberately editable, so it is not part of this check.
  return valid ? kind : "custom";
}

export function patchGeneralLeg(
  leg: GeneralLeg,
  patch: Partial<GeneralLeg>,
): GeneralLeg {
  const contractChanged =
    (patch.strike !== undefined && patch.strike !== leg.strike) ||
    (patch.type !== undefined && patch.type !== leg.type) ||
    (patch.expiry !== undefined && patch.expiry !== leg.expiry);
  return { ...leg, ...patch, ...(contractChanged ? { premium: null } : {}) };
}

export function offsetGeneralDraft(
  draft: GeneralDraft,
  offset: number,
): GeneralDraft {
  const delta = offset - (draft.strikeOffset ?? 0);
  if (!Number.isFinite(delta) || delta === 0) return draft;
  const legs = draft.legs.map((leg) => ({
    ...leg,
    strike: Number((leg.strike + delta).toFixed(8)),
  }));
  if (legs.some((leg) => !Number.isFinite(leg.strike) || leg.strike <= 0))
    return draft;
  return { ...draft, strikeOffset: offset, legs };
}

export function decodeGeneralDraft(
  layer: StrategyLayer,
  anchor: number,
  step: number,
  holidays: Set<string>,
): GeneralDraft {
  const entry = getRegistryEntry(layer.kind);
  const raw = applyLegOverrides(
    applyLegDeltas(
      entry.toLegs(effectiveSpot(anchor, layer.params), layer.params, step),
      layer.params,
    ),
    layer.params,
  );
  const draft: GeneralDraft = {
    id: layer.id,
    name: layer.label ?? entry.label,
    structureKind:
      GENERAL_KINDS[layer.params.general_structure_kind - 1] ?? layer.kind,
    quantity: layer.params.contracts ?? 1,
    enabled: layer.enabled,
    color: layer.color,
    ...(Number.isFinite(layer.params.general_strike_offset)
      ? { strikeOffset: layer.params.general_strike_offset }
      : {}),
    ...(Number.isFinite(layer.params.general_reference_price)
      ? { referencePrice: layer.params.general_reference_price }
      : {}),
    legs: raw.flatMap((leg, index) =>
      layer.params[`leg_off_${index}`] === 1
        ? []
        : [
            {
              ...leg,
              id: `${layer.id}:${index}`,
              expiry:
                resolveLegExpiry(layer, leg, 0, holidays, index).expiry ??
                toET(new Date()).dateStr,
              premium: Number.isFinite(layer.params[`premium_leg_${index}`])
                ? layer.params[`premium_leg_${index}`]
                : null,
            },
          ],
    ),
  };
  draft.source = {
    layer: { ...layer, params: { ...layer.params } },
    legs: draft.legs.map((leg) => ({ ...leg })),
  };
  return draft;
}

// Use the existing custom-layer representation so both interfaces read the same contracts.
export function encodeGeneralDraft(
  draft: GeneralDraft,
  anchor: number,
  step: number,
  now = new Date(),
): StrategyLayer {
  // Preserve the flagship's structure and market anchoring when editing its
  // existing contracts. A custom encoding is only needed for new topology or
  // expiry changes, not an ordinary save, rename, quantity or premium edit.
  const source = draft.source;
  if (
    source &&
    draft.legs.every((leg) =>
      source.legs.some(
        (original) => original.id === leg.id && original.expiry === leg.expiry,
      ),
    )
  ) {
    const params = { ...source.layer.params };
    const structureCode = GENERAL_KINDS.indexOf(generalDraftKind(draft)) + 1;
    if (
      structureCode > 0 &&
      (source.layer.kind === "custom" ||
        source.layer.params.general_structure_kind !== undefined)
    )
      params.general_structure_kind = structureCode;
    if (draft.strikeOffset !== undefined)
      params.general_strike_offset = draft.strikeOffset;
    if (draft.referencePrice !== undefined)
      params.general_reference_price = draft.referencePrice;
    if (draft.quantity !== (params.contracts ?? 1))
      params.contracts = draft.quantity;
    source.legs.forEach((original) => {
      const index = Number(original.id.slice(`${source.layer.id}:`.length));
      const leg = draft.legs.find((item) => item.id === original.id);
      if (!leg) {
        params[`leg_off_${index}`] = 1;
        return;
      }
      if (leg.strike !== original.strike)
        params[`d_leg_${index}`] =
          (params[`d_leg_${index}`] ?? 0) + leg.strike - original.strike;
      if (leg.type !== original.type)
        params[`t_leg_${index}`] = leg.type === "put" ? 1 : 0;
      if (leg.side !== original.side)
        params[`s_leg_${index}`] = leg.side === "short" ? -1 : 1;
      if (leg.quantity !== original.quantity)
        params[`q_leg_${index}`] = leg.quantity;
      if (leg.premium !== original.premium) {
        if (leg.premium === null) delete params[`premium_leg_${index}`];
        else params[`premium_leg_${index}`] = leg.premium;
      }
    });
    return {
      ...source.layer,
      params,
      label:
        draft.name ===
        (source.layer.label ?? getRegistryEntry(source.layer.kind).label)
          ? source.layer.label
          : draft.name.trim(),
      enabled: draft.enabled,
      color: draft.color,
    };
  }
  const earliestExpiry = Math.min(
    ...draft.legs.map((leg) => expiryCloseMs(leg.expiry)),
  );
  const entryTime = Math.min(now.getTime(), earliestExpiry - 86_400_000);
  const params = encodeLegsAsCustomParams(draft.legs, anchor, step);
  const structureCode = GENERAL_KINDS.indexOf(generalDraftKind(draft)) + 1;
  if (structureCode > 0) params.general_structure_kind = structureCode;
  if (draft.strikeOffset !== undefined)
    params.general_strike_offset = draft.strikeOffset;
  Object.assign(params, {
    contracts: draft.quantity,
    anchor_locked: 1,
    expiry_anchor_ms: entryTime,
  });
  if (draft.referencePrice !== undefined)
    params.general_reference_price = draft.referencePrice;
  if (!draft.legs.length) Object.assign(params, { leg_count: 1, leg_off_0: 1 });
  draft.legs.forEach((leg, index) => {
    params[`q_leg_${index}`] = leg.quantity;
    params[`expiry_leg_${index}`] = calendarDteAtCursor(
      entryTime,
      expiryCloseMs(leg.expiry),
    );
    if (leg.premium !== null) params[`premium_leg_${index}`] = leg.premium;
  });
  params.expiryDTE = params.expiry_leg_0 ?? 0;
  return {
    id: draft.id,
    kind: "custom",
    params,
    label: draft.name.trim(),
    enabled: draft.enabled,
    color: draft.color,
  };
}

export function createGeneralDraft(
  kind: StrategyLayerKind,
  name: string,
  anchor: number,
  step: number,
  expiry: string,
): GeneralDraft {
  const entry = getRegistryEntry(kind);
  return {
    id: crypto.randomUUID(),
    name,
    structureKind: kind,
    quantity: 1,
    enabled: true,
    color: "#d4f88a",
    legs:
      kind === "custom"
        ? []
        : entry
            .toLegs(anchor, generalTemplateParams(kind, anchor, step), step)
            .map((leg) => ({
              ...leg,
              id: crypto.randomUUID(),
              expiry,
              premium: null,
            })),
  };
}

export function generalPayoff(draft: GeneralDraft) {
  if (
    !draft.legs.length ||
    draft.legs.some((l) => l.premium === null) ||
    new Set(draft.legs.map((l) => l.expiry)).size > 1
  )
    return null;
  const credit = draft.legs.reduce(
    (sum, l) =>
      sum + (l.side === "short" ? 1 : -1) * (l.premium ?? 0) * l.quantity,
    0,
  );
  const at = (price: number) =>
    payoffAtExpiry(draft.legs, credit, price) * draft.quantity;
  const strikes = draft.legs.map((l) => l.strike);
  const boundaries = [0, ...strikes].map(at);
  const callSlope = draft.legs
    .filter((l) => l.type === "call")
    .reduce((sum, l) => sum + (l.side === "long" ? 1 : -1) * l.quantity, 0);
  return {
    at,
    credit: credit * 100 * draft.quantity,
    maxProfit: callSlope > 0 ? Infinity : Math.max(...boundaries),
    maxLoss: callSlope < 0 ? -Infinity : Math.min(...boundaries),
  };
}
